import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Check, Play } from "lucide-react";
import api from "@/lib/api";

function Dot({ state }) {
  if (state === "complete")
    return <span className="mrow__dot dot--complete" aria-label="Completed"><Check size={12} strokeWidth={3} /></span>;
  if (state === "current")
    return <span className="mrow__dot dot--current" aria-label="Current" />;
  return <span className="mrow__dot dot--todo" aria-label="Not started" />;
}

export default function ModulePlayer() {
  const { pid, rid, sid, mid } = useParams();
  const { setCrumbs } = useOutletContext();
  const navigate = useNavigate();
  const [mod, setMod] = useState(null);
  const [path, setPath] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setMod(null);
    Promise.all([
      api.get(`/catalog/${pid}/${rid}/${sid}/${mid}`),
      api.get(`/catalog/${pid}/${rid}`),
      api.get(`/progress/${pid}/${rid}`),
    ]).then(([m, p, pr]) => {
      setMod(m.data);
      setPath(p.data);
      setCompleted(new Set(pr.data.completed));
      setCrumbs([
        { label: m.data.product.name, to: `/${pid}` },
        { label: m.data.role.name, to: `/${pid}/${rid}` },
        { label: m.data.module.title, to: null },
      ]);
      window.scrollTo(0, 0);
    }).catch(() => navigate(`/${pid}/${rid}`, { replace: true }));
  }, [pid, rid, sid, mid, navigate, setCrumbs]);

  useEffect(() => { load(); }, [load]);

  if (!mod || !path) return <div className="loading">Loading the module…</div>;

  const si = path.sections.findIndex((s) => s.id === sid);
  const section = path.sections[si];
  const mi = section.modules.findIndex((m) => m.id === mid);
  const keyFor = (a, b) => `${pid}|${rid}|${a}|${b}`;

  // current across the whole path
  let currentKey = null;
  outer: for (let a = 0; a < path.sections.length; a++) {
    for (let b = 0; b < path.sections[a].modules.length; b++) {
      if (!completed.has(keyFor(a, b))) { currentKey = keyFor(a, b); break outer; }
    }
  }
  const siblingState = (b) => {
    const k = keyFor(si, b);
    if (completed.has(k)) return "complete";
    if (k === currentKey) return "current";
    return "todo";
  };

  const isLast = mi === section.modules.length - 1;
  const completeLabel = isLast ? "Mark complete and take the check" : "Mark complete and continue";

  const markComplete = async () => {
    setBusy(true);
    try {
      await api.post(`/progress/${pid}/${rid}/module`, { sectionId: sid, moduleId: mid });
      if (isLast) navigate(`/${pid}/${rid}/${sid}/check`);
      else navigate(`/${pid}/${rid}/${sid}/${section.modules[mi + 1].id}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="page" data-testid="module-player">
      <div className="container mplayer">
        <div className="mplayer__main">
          <div className="mplayer__head">
            <div className="mplayer__crumb">
              <span>{mod.product.tag} · {mod.role.name} · {mod.section.title}</span>
              {mod.module.essential && <span className="etag">Essential</span>}
            </div>
            <h1 className="mplayer__title" data-testid="module-title">{mod.module.title}</h1>
          </div>

          <div className="video on-dark" data-testid="module-video">
            <div className="video__label">Screen recording · seeded demo company</div>
            <button className="video__play" aria-label="Play module video" data-testid="module-play">
              <Play size={26} fill="var(--eq-black)" strokeWidth={0} />
            </button>
          </div>
          <div className="scrub">
            <span className="scrub__time">00:00</span>
            <span className="scrub__bar"><span className="scrub__fill" style={{ width: "0%" }} /></span>
            <span className="scrub__time">{mod.module.duration}</span>
          </div>

          <div className="summary">
            <div className="eyebrow" style={{ marginBottom: "12px" }}>Written summary</div>
            <p className="summary__text">{mod.module.summaryHtml}</p>
            <div className="summary__downloads">
              {mod.module.downloads.map((d, i) => (
                <button key={i} className="dl" data-testid={`download-${i}`}>{d}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="mplayer__side">
          <div className="sidecard">
            <div className="sidecard__label">In this section</div>
            <div className="scard__list">
              {section.modules.map((m, b) => {
                const st = siblingState(b);
                return (
                  <button key={m.id} className={`mrow mrow--${st}`} onClick={() => navigate(`/${pid}/${rid}/${sid}/${m.id}`)} data-testid={`sibling-${m.id}`}>
                    <Dot state={st} />
                    <span className="mrow__title">{m.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="sidecard">
            <div className="actionstack">
              <button className="btn btn--dark btn--block" onClick={markComplete} disabled={busy} data-testid="mark-complete-btn">
                {busy ? "Saving…" : completeLabel}
              </button>
              <button className="btn btn--outline btn--block" onClick={() => navigate(`/${pid}/${rid}`)} data-testid="back-to-path-btn">
                Back to the path
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
